import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// withCredentials sends the httpOnly session_token cookie automatically.
// We no longer read the token from localStorage (mitigates XSS token theft).
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Background/polling endpoints that should NOT trigger logout on 401
const SILENT_ENDPOINTS = ['/notifications', '/auth/me'];

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const isSilent = SILENT_ENDPOINTS.some(ep => requestUrl.includes(ep));

      const detail = error.response?.data?.detail || '';
      const isSessionInvalid = detail.includes('Invalid session') || detail.includes('Session expired') || detail.includes('Not authenticated');

      if (!isSilent && isSessionInvalid) {
        localStorage.removeItem('user');
        const isProtectedRoute = ['/dashboard', '/projects', '/tasks', '/users', '/settings', '/invoices', '/calendar', '/vendors', '/gantt', '/client-dashboard'].some(
          route => window.location.pathname.startsWith(route)
        );
        if (isProtectedRoute && !window.location.hash.includes('session_id')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  processSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Users API
export const usersAPI = {
  list: () => api.get('/users'),
  teamMembers: () => api.get('/users/team-members'),
  create: (data) => api.post('/users', data),
  get: (userId) => api.get(`/users/${userId}`),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
  resetPassword: (userId, password) => api.put(`/users/${userId}/reset-password`, { password }),
};

// Projects API
export const projectsAPI = {
  list: () => api.get('/projects'),
  create: (data) => api.post('/projects', data),
  get: (projectId) => api.get(`/projects/${projectId}`),
  update: (projectId, data) => api.put(`/projects/${projectId}`, data),
  delete: (projectId) => api.delete(`/projects/${projectId}`),
  uploadFile: (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${projectId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteFile: (projectId, fileId) => api.delete(`/projects/${projectId}/files/${fileId}`),
};

// Tasks API
export const tasksAPI = {
  list: (projectId = null) => api.get('/tasks', { params: projectId ? { project_id: projectId } : {} }),
  create: (data) => api.post('/tasks', data),
  get: (taskId) => api.get(`/tasks/${taskId}`),
  update: (taskId, data) => api.put(`/tasks/${taskId}`, data),
  delete: (taskId) => api.delete(`/tasks/${taskId}`),
};

// Notifications API
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  markRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Checklists API
export const checklistsAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/checklists`),
  create: (projectId, data) => api.post(`/projects/${projectId}/checklists`, data),
  get: (checklistId) => api.get(`/checklists/${checklistId}`),
  update: (checklistId, data) => api.put(`/checklists/${checklistId}`, data),
  delete: (checklistId) => api.delete(`/checklists/${checklistId}`),
  addItem: (checklistId, data) => api.post(`/checklists/${checklistId}/items`, data),
  updateItem: (checklistId, itemId, data) => api.put(`/checklists/${checklistId}/items/${itemId}`, data),
  deleteItem: (checklistId, itemId) => api.delete(`/checklists/${checklistId}/items/${itemId}`),
  updateItemStatus: (checklistId, itemId, data) => api.put(`/checklists/${checklistId}/items/${itemId}/status`, data),
};

// Budget API
export const budgetAPI = {
  get: (projectId) => api.get(`/projects/${projectId}/budget`),
  addItem: (projectId, data) => api.post(`/projects/${projectId}/budget`, data),
  updateItem: (itemId, data) => api.put(`/budget-items/${itemId}`, data),
  deleteItem: (itemId) => api.delete(`/budget-items/${itemId}`),
};

// Invoice API
export const invoicesAPI = {
  list: () => api.get('/invoices'),
  listByProject: (projectId) => api.get(`/projects/${projectId}/invoices`),
  get: (invoiceId) => api.get(`/invoices/${invoiceId}`),
  create: (data) => api.post('/invoices', data),
  update: (invoiceId, data) => api.put(`/invoices/${invoiceId}`, data),
  delete: (invoiceId) => api.delete(`/invoices/${invoiceId}`),
};

// Comments API
export const commentsAPI = {
  list: (entityType, entityId) => api.get(`/comments?entity_type=${entityType}&entity_id=${entityId}`),
  create: (data) => api.post('/comments', data),
  delete: (commentId) => api.delete(`/comments/${commentId}`),
};

// Activity Log API
export const activityAPI = {
  getProjectActivity: (projectId) => api.get(`/projects/${projectId}/activity`),
};

// Approvals API
export const approvalsAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/approvals`),
  create: (projectId, data) => api.post(`/projects/${projectId}/approvals`, data),
  respond: (approvalId, data) => api.put(`/approvals/${approvalId}/respond`, data),
};

// Change Orders API
export const changeOrdersAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/change-orders`),
  create: (projectId, data) => api.post(`/projects/${projectId}/change-orders`, data),
  update: (orderId, data) => api.put(`/change-orders/${orderId}`, data),
  delete: (orderId) => api.delete(`/change-orders/${orderId}`),
};

// Client Dashboard API
export const clientDashboardAPI = {
  get: () => api.get('/client/dashboard'),
};

// Time Tracking API
export const timeTrackingAPI = {
  listByProject: (projectId) => api.get(`/projects/${projectId}/time-entries`),
  listByTask: (taskId) => api.get(`/tasks/${taskId}/time-entries`),
  create: (data) => api.post('/time-entries', data),
  delete: (entryId) => api.delete(`/time-entries/${entryId}`),
  summary: () => api.get('/time-entries/summary'),
};

// Vendor API
export const vendorsAPI = {
  list: () => api.get('/vendors'),
  get: (vendorId) => api.get(`/vendors/${vendorId}`),
  create: (data) => api.post('/vendors', data),
  update: (vendorId, data) => api.put(`/vendors/${vendorId}`, data),
  delete: (vendorId) => api.delete(`/vendors/${vendorId}`),
  // Articles
  listArticles: (vendorId) => api.get(`/vendors/${vendorId}/articles`),
  createArticle: (vendorId, data) => api.post(`/vendors/${vendorId}/articles`, data),
  updateArticle: (articleId, data) => api.put(`/vendors/articles/${articleId}`, data),
  deleteArticle: (articleId) => api.delete(`/vendors/articles/${articleId}`),
  // Price History
  getPriceHistory: (articleId) => api.get(`/vendors/articles/${articleId}/price-history`),
  addPriceHistory: (articleId, data) => api.post(`/vendors/articles/${articleId}/price-history`, data),
  // Orders
  listAllOrders: (params) => api.get('/vendors/orders/all', { params }),
  listVendorOrders: (vendorId) => api.get(`/vendors/${vendorId}/orders`),
  createOrder: (vendorId, data) => api.post(`/vendors/${vendorId}/orders`, data),
  updateOrder: (orderId, data) => api.put(`/vendors/orders/${orderId}`, data),
  deleteOrder: (orderId) => api.delete(`/vendors/orders/${orderId}`),
  // Comparison
  compareArticles: () => api.get('/vendors/compare/articles'),
};

// Mood Board API
export const moodBoardsAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/mood-boards`),
  create: (projectId, data) => api.post(`/projects/${projectId}/mood-boards`, data),
  update: (boardId, data) => api.put(`/mood-boards/${boardId}`, data),
  addImage: (boardId, data) => api.post(`/mood-boards/${boardId}/images`, data),
  updateImage: (boardId, imageId, data) => api.put(`/mood-boards/${boardId}/images/${imageId}`, data),
  deleteImage: (boardId, imageId) => api.delete(`/mood-boards/${boardId}/images/${imageId}`),
  delete: (boardId) => api.delete(`/mood-boards/${boardId}`),
};

// Document API
export const documentsAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/documents`),
  create: (projectId, data) => api.post(`/projects/${projectId}/documents`, data),
  update: (docId, data) => api.put(`/documents/${docId}`, data),
  delete: (docId) => api.delete(`/documents/${docId}`),
};

// Calendar API
export const calendarAPI = {
  getTasks: () => api.get('/calendar/tasks'),
};

// Expenditure API
export const expenditureAPI = {
  getSummary: (params) => api.get('/expenditure/summary', { params }),
};

// Rooms API
export const roomsAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/rooms`),
  create: (projectId, data) => api.post(`/projects/${projectId}/rooms`, data),
  update: (roomId, data) => api.put(`/rooms/${roomId}`, data),
  delete: (roomId) => api.delete(`/rooms/${roomId}`),
  types: () => api.get('/rooms/types'),
};

// Phases API
export const phasesAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/phases`),
  create: (projectId, data) => api.post(`/projects/${projectId}/phases`, data),
  createDefaults: (projectId) => api.post(`/projects/${projectId}/phases/defaults`),
  update: (phaseId, data) => api.put(`/phases/${phaseId}`, data),
  delete: (phaseId) => api.delete(`/phases/${phaseId}`),
};

// Proposals API
export const proposalsAPI = {
  list: () => api.get('/proposals'),
  get: (proposalId) => api.get(`/proposals/${proposalId}`),
  create: (data) => api.post('/proposals', data),
  update: (proposalId, data) => api.put(`/proposals/${proposalId}`, data),
  delete: (proposalId) => api.delete(`/proposals/${proposalId}`),
  convertToInvoice: (proposalId) => api.post(`/proposals/${proposalId}/convert-to-invoice`),
};

// Reports API
export const reportsAPI = {
  financial: () => api.get('/reports/financial'),
};

// FF&E API
export const ffeAPI = {
  list: (params) => api.get('/ffe', { params }),
  summary: () => api.get('/ffe/summary'),
  create: (data) => api.post('/ffe', data),
  update: (ffeId, data) => api.put(`/ffe/${ffeId}`, data),
  delete: (ffeId) => api.delete(`/ffe/${ffeId}`),
};

// AI API
export const aiAPI = {
  budgetEstimate: (data) => api.post('/ai/budget-estimate', data),
  vendorRecommendations: (data) => api.post('/ai/vendor-recommendations', data),
  generateFFESchedule: (data) => api.post('/ai/generate-ffe-schedule', data),
};

// Signatures API
export const signaturesAPI = {
  sign: (docType, docId, data) => api.post(`/signatures/${docType}/${docId}`, data),
  get: (docType, docId) => api.get(`/signatures/${docType}/${docId}`),
};

// Site Diary API
export const siteDiaryAPI = {
  list: (projectId, params) => api.get(`/site-diary/projects/${projectId}`, { params }),
  create: (projectId, data) => api.post(`/site-diary/projects/${projectId}`, data),
  update: (entryId, data) => api.put(`/site-diary/${entryId}`, data),
  delete: (entryId) => api.delete(`/site-diary/${entryId}`),
};

// Magic Links API
export const magicLinksAPI = {
  list: () => api.get('/magic-links'),
  create: (data) => api.post('/magic-links', data),
  delete: (linkId) => api.delete(`/magic-links/${linkId}`),
  toggle: (linkId) => api.put(`/magic-links/${linkId}/toggle`),
  access: (token) => api.get(`/magic-links/access/${token}`),
};

// Contracts API
export const contractsAPI = {
  list: () => api.get('/contracts'),
  templates: () => api.get('/contracts/templates'),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  sign: (id, data) => api.post(`/contracts/${id}/sign`, data),
};

// Product Clipper API
export const productClipperAPI = {
  scrape: (data) => api.post('/product-clipper/scrape', data),
  enhance: (data) => api.post('/product-clipper/enhance', data),
};

// CRM / Leads API
export const crmAPI = {
  listLeads: () => api.get('/crm/leads'),
  createLead: (data) => api.post('/crm/leads', data),
  getLead: (leadId) => api.get(`/crm/leads/${leadId}`),
  updateLead: (leadId, data) => api.put(`/crm/leads/${leadId}`, data),
  addActivity: (leadId, data) => api.post(`/crm/leads/${leadId}/activity`, data),
  deleteLead: (leadId) => api.delete(`/crm/leads/${leadId}`),
  convertToProject: (leadId, data) => api.post(`/crm/leads/${leadId}/convert`, data),
  stats: () => api.get('/crm/stats'),
};

// Assemblies API
export const assembliesAPI = {
  list: () => api.get('/assemblies'),
  get: (id) => api.get(`/assemblies/${id}`),
  create: (data) => api.post('/assemblies', data),
  update: (id, data) => api.put(`/assemblies/${id}`, data),
  delete: (id) => api.delete(`/assemblies/${id}`),
  clone: (id) => api.post(`/assemblies/${id}/clone`),
};

// Project Templates API
export const projectTemplatesAPI = {
  list: () => api.get('/project-templates'),
  saveFromProject: (projectId, data) => api.post(`/project-templates/save/${projectId}`, data),
  clone: (templateId, data) => api.post(`/project-templates/clone/${templateId}`, data),
  delete: (templateId) => api.delete(`/project-templates/${templateId}`),
};

// Bulk CSV Import API
export const bulkImportAPI = {
  importFFE: (data) => api.post('/import/ffe', data),
  importVendors: (data) => api.post('/import/vendors', data),
  importClients: (data) => api.post('/import/clients', data),
  importTasks: (data) => api.post('/import/tasks', data),
  templateFFE: () => api.get('/import/template/ffe'),
  templateVendors: () => api.get('/import/template/vendors'),
  templateClients: () => api.get('/import/template/clients'),
  templateTasks: () => api.get('/import/template/tasks'),
};

// Calendar Sync (.ics + Google) API
export const calendarSyncAPI = {
  getSubscriptionUrl: () => api.get('/calendar-sync/subscription-url'),
  downloadIcs: (projectId = null) => api.get('/calendar-sync/export.ics', {
    params: projectId ? { project_id: projectId } : {},
    responseType: 'blob',
  }),
  googleStatus: () => api.get('/google-calendar/status'),
  googleAuthorize: () => api.get('/google-calendar/authorize'),
  googleDisconnect: () => api.post('/google-calendar/disconnect'),
  googlePush: () => api.post('/google-calendar/push'),
  googlePull: () => api.get('/google-calendar/pull'),
};

// Purchase Orders API
export const purchaseOrdersAPI = {
  list: () => api.get('/purchase-orders'),
  create: (data) => api.post('/purchase-orders', data),
  update: (poId, data) => api.put(`/purchase-orders/${poId}`, data),
  updateStatus: (poId, data) => api.put(`/purchase-orders/${poId}/status`, data),
  delete: (poId) => api.delete(`/purchase-orders/${poId}`),
};

// Help & Support API
export const helpAPI = {
  listFaqs: (params) => api.get('/help/faqs', { params }),
  createFaq: (data) => api.post('/help/faqs', data),
  updateFaq: (id, data) => api.put(`/help/faqs/${id}`, data),
  deleteFaq: (id) => api.delete(`/help/faqs/${id}`),
  viewFaq: (id) => api.post(`/help/faqs/${id}/view`),
  categories: () => api.get('/help/categories'),
  chat: (data) => api.post('/help/chat', data),
  chatHistory: (sessionId) => api.get('/help/chat/history', { params: { session_id: sessionId } }),
  analytics: (days = 30) => api.get('/help/analytics', { params: { days } }),
  listGuides: () => api.get('/help/guides'),
  getGuide: (role) => api.get(`/help/guides/${role}`),
  listSuggestions: () => api.get('/help/suggested-faqs'),
  generateSuggestions: (data) => api.post('/help/suggested-faqs/generate', data || {}),
  approveSuggestion: (id, data) => api.post(`/help/suggested-faqs/${id}/approve`, data || {}),
  dismissSuggestion: (id) => api.post(`/help/suggested-faqs/${id}/dismiss`),
};

export default api;
